import React, { useState } from 'react';
import { Monitor, Bot, Workflow, Rocket, Check, Smartphone } from 'lucide-react';
import { FEATURE_LIST } from '../mock/mock';

const ICONS = { Monitor, Bot, Workflow, Rocket };

export default function Features() {
  const [active, setActive] = useState(FEATURE_LIST[0].id);
  const activeFeature = FEATURE_LIST.find((f) => f.id === active);

  return (
    <section id="features" className="relative py-24 lg:py-32 bg-white">
      <div className="mx-auto max-w-[1400px] px-6 lg:px-10">
        <div className="text-center max-w-2xl mx-auto mb-16">
          <h2 className="font-display text-4xl lg:text-5xl font-bold text-neutral-900 tracking-tight">
            What can Transformerus do for you?
          </h2>
          <p className="mt-4 text-lg text-neutral-600">
            From concept to deployment, Transformerus handles every aspect of software development so you can focus on what matters most — your vision.
          </p>
        </div>

        <div className="grid lg:grid-cols-2 gap-12 lg:gap-20 items-center">
          <div className="space-y-2">
            {FEATURE_LIST.map((f) => {
              const Icon = ICONS[f.icon];
              const isActive = active === f.id;
              return (
                <button
                  key={f.id}
                  onClick={() => setActive(f.id)}
                  className={`w-full text-left p-5 rounded-2xl transition-all border ${
                    isActive ? 'bg-neutral-50 border-neutral-200' : 'bg-transparent border-transparent hover:bg-neutral-50/60'
                  }`}
                >
                  <div className="flex items-start gap-4">
                    <div className={`w-10 h-10 rounded-xl flex items-center justify-center transition-colors ${
                      isActive ? 'bg-neutral-900 text-white' : 'bg-neutral-100 text-neutral-700'
                    }`}>
                      <Icon className="w-5 h-5" />
                    </div>
                    <div className="flex-1">
                      <h3 className="font-display text-xl font-semibold text-neutral-900">{f.title}</h3>
                      <div className={`overflow-hidden transition-all ${isActive ? 'max-h-40 mt-2' : 'max-h-0'}`}>
                        <p className="text-neutral-600 text-sm leading-relaxed">{f.desc}</p>
                      </div>
                    </div>
                  </div>
                </button>
              );
            })}
          </div>

          {/* Right showcase visual */}
          <div className="relative aspect-[4/5] max-w-md mx-auto w-full">
            <div className="absolute inset-0 rounded-[40px] bg-gradient-to-br from-sky-100 via-cyan-50 to-white border border-neutral-200 overflow-hidden">
              <div className="absolute inset-0 bg-[radial-gradient(circle_at_70%_30%,rgba(125,211,252,0.4),transparent_60%)]" />
            </div>

            {/* Phone mockup */}
            <div className="absolute left-1/2 top-8 -translate-x-1/2 w-[260px] h-[520px] bg-neutral-900 rounded-[40px] p-2 shadow-2xl">
              <div className="w-full h-full bg-white rounded-[32px] overflow-hidden relative">
                <div className="px-5 py-4 border-b border-neutral-100">
                  <div className="flex items-center justify-between">
                    <div className="text-xs font-semibold text-neutral-900">{activeFeature.title.split(' ')[1] || 'Library'}</div>
                    <Smartphone className="w-4 h-4 text-neutral-400" />
                  </div>
                </div>
                <div className="p-4 grid grid-cols-2 gap-3">
                  {[0, 1, 2, 3, 4, 5].map((i) => (
                    <div key={i} className={`aspect-[3/4] rounded-lg bg-gradient-to-br ${
                      ['from-rose-200 to-amber-200', 'from-sky-200 to-indigo-200', 'from-emerald-200 to-teal-200', 'from-fuchsia-200 to-pink-200', 'from-amber-200 to-orange-200', 'from-cyan-200 to-blue-200'][i]
                    } shadow-sm`} />
                  ))}
                </div>
                <div className="absolute bottom-0 inset-x-0 px-5 py-3 border-t border-neutral-100 bg-white flex items-center justify-around">
                  {[0, 1, 2, 3].map((i) => (
                    <div key={i} className="w-6 h-6 rounded-md bg-neutral-100" />
                  ))}
                </div>
              </div>
            </div>

            {/* Floating chips */}
            <div className="absolute top-12 right-2 px-3 py-2 rounded-xl bg-white shadow-lg border border-neutral-200 flex items-center gap-2 animate-float">
              <Check className="w-4 h-4 text-emerald-500" />
              <span className="text-xs font-medium">Deployed</span>
            </div>
            <div className="absolute bottom-16 left-0 px-3 py-2 rounded-xl bg-white shadow-lg border border-neutral-200 flex items-center gap-2 animate-float" style={{ animationDelay: '1s' }}>
              <div className="w-2 h-2 rounded-full bg-cyan-500" />
              <span className="text-xs font-medium">Live preview</span>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
